import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthTokenService } from '@core/auth/auth-token.service';
import { SUPERADMIN_ELEVATION_HEADER } from '@core/auth/superadmin-elevation.model';
import { SuperAdminElevationService } from '@core/auth/superadmin-elevation.service';
import { API_URL } from '@core/config/api-url.token';
import { isOwnApiRequest } from '@core/http/is-own-api-request.util';
import { ANONYMOUS_REQUEST } from '@core/http/anonymous-request.context';
import { API_ENDPOINTS } from '@shared/constants/api-endpoints.const';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthRefreshService } from '../services/auth-refresh.service';

const SUPERADMIN_API_PREFIX = '/api/superadmin/v1';
const PASSKEYS_API_PREFIX = '/api/auth/passkeys';

// The two anonymous passkey routes: no session exists yet when they run, so there is no elevation
// to send and nothing to gate.
const ELEVATION_EXEMPT_PASSKEY_PATHS: readonly string[] = [
  API_ENDPOINTS.auth.passkeys.login,
  API_ENDPOINTS.auth.passkeys.assertionOptions,
];

// Compared as a PATH, not as a substring of the whole URL: `API_URL` is operator-configured, so a
// naive `url.includes('/api/superadmin/v1')` would happily attach the console's step-up credential
// to a request aimed at some unrelated host that merely happens to contain that string.
function needsElevationHeader(url: string, apiUrl: string): boolean {
  const path = (apiUrl && url.startsWith(apiUrl) ? url.slice(apiUrl.length) : url).split(/[?#]/)[0];

  if (path.startsWith(SUPERADMIN_API_PREFIX)) return true;
  if (!path.startsWith(PASSKEYS_API_PREFIX)) return false;
  return !ELEVATION_EXEMPT_PASSKEY_PATHS.includes(path);
}

function buildHeaders(
  accessToken: string | undefined, acceptLanguage: string, elevationToken: string | null,
): Record<string, string> {
  const headers: Record<string, string> = { 'Accept-Language': acceptLanguage };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  if (elevationToken) {
    headers[SUPERADMIN_ELEVATION_HEADER] = elevationToken;
  }

  return headers;
}

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  if (request.context.get(ANONYMOUS_REQUEST)) return next(request);
  const tokenService = inject(AuthTokenService);
  const refreshService = inject(AuthRefreshService);
  const elevationService = inject(SuperAdminElevationService);
  const apiUrl = inject(API_URL);
  const translocoService = inject(TranslocoService);

  if (!isOwnApiRequest(request.url, apiUrl)) {
    return next(request);
  }

  const acceptLanguage = translocoService.getActiveLang();
  const accessToken = tokenService.tokens()?.accessToken;
  const elevationToken = needsElevationHeader(request.url, apiUrl) ? elevationService.token() : null;
  const authorizedRequest = request.clone({
    setHeaders: buildHeaders(accessToken, acceptLanguage, elevationToken),
  });

  return next(authorizedRequest).pipe(
    catchError((error: unknown) => {
      const refreshToken = tokenService.tokens()?.refreshToken;
      // The two anonymous passkey routes must be listed explicitly: this is a substring test, and
      // `'/api/auth/passkeys/login'.includes('/api/auth/login')` is FALSE, so without them a
      // rejected passkey assertion (a 401 by design -- wrong credential, expired challenge) would
      // fire a refresh-and-retry and, when that refresh failed, log the user out of a session they
      // were only trying to start.
      const isAuthRequest = request.url.includes(API_ENDPOINTS.auth.login)
        || request.url.includes(API_ENDPOINTS.auth.refresh)
        || request.url.includes(API_ENDPOINTS.auth.logout)
        || request.url.includes(API_ENDPOINTS.auth.passkeys.login)
        || request.url.includes(API_ENDPOINTS.auth.passkeys.assertionOptions);

      // Only 401s enter the refresh path. A missing elevation answers 403 precisely so it never
      // does: an expired step-up must send the user back to the console's verify step, not log
      // them out of the whole app.
      if (!(error instanceof HttpErrorResponse) || error.status !== 401 || !refreshToken || isAuthRequest) {
        return throwError(() => error);
      }

      return refreshService
        .refresh()
        .pipe(
          switchMap(newAccessToken =>
            // The elevation has to be re-attached here too -- the retry is a fresh clone of the
            // ORIGINAL request, so without this it would arrive unelevated and 403.
            next(request.clone({
              setHeaders: buildHeaders(newAccessToken, acceptLanguage, elevationToken),
            })),
          ),
        );
    }),
  );
};
